import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  AlertCircle,
  AlertTriangle,
  Blocks,
  Building2,
  Check,
  CheckCircle2,
  Loader2,
  Lock,
  MapPin,
  Users,
} from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx } from "../../utils";
import AppIcon from "../../components/ui/AppIcon";

/**
 * Marketplace install callback.
 *
 * The CRM redirects the installer here after they authorize the app:
 *
 *   /integration/<your app id>?code=<one-time code>
 *
 * The CRM appends `code` itself; `appId` is a path segment baked into the
 * redirect URI registered for each app. It has to come from us because the
 * token response identifies the company and location but never which app was
 * installed, and the backend needs that app's client secret before it can
 * exchange the code at all.
 *
 * The exchange itself happens server-side (POST /auth/integrate_app). Doing it
 * here would mean shipping the app's client secret to the browser.
 *
 * This is a dead end by design: the installer lands here in a browser tab after
 * authorizing, so there is nowhere useful to send them. It confirms the install
 * and hands over whatever the app's admin wrote as the next step.
 */

// An agency-wide install sets up every account one by one before it answers,
// which can take a while — past this point the page says so.
const SLOW_AFTER_MS = 8000;

/** The page's backdrop and centred column, shared by every state. */
const Shell = ({ children }) => (
  <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink-50 px-4 py-10 sm:px-6">
    <div className="pointer-events-none absolute inset-0 bg-mesh-1" aria-hidden />
    <div
      className="pointer-events-none absolute inset-0 bg-dotted bg-dot-md opacity-60 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
      aria-hidden
    />
    <main className="relative w-full max-w-2xl anim-in">{children}</main>
  </div>
);

/** One row of the progress checklist shown while the install completes. */
const Step = ({ state, title, detail }) => (
  <li className="flex gap-3.5">
    <span
      className={cx(
        "mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full",
        state === "done" && "bg-emerald-500 text-white shadow-sm",
        state === "active" && "bg-brand-50 text-brand-600 ring-1 ring-brand-200",
        state === "waiting" && "bg-ink-100 text-ink-400",
      )}
      aria-hidden
    >
      {state === "done" ? (
        <Check size={14} strokeWidth={3} />
      ) : state === "active" ? (
        <Loader2 size={14} className="animate-spin motion-reduce:animate-none" />
      ) : (
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
      )}
    </span>
    <div className="min-w-0">
      <p className={cx("text-[14px] font-semibold", state === "waiting" ? "text-ink-400" : "text-ink-900")}>{title}</p>
      {detail && <p className="mt-0.5 text-[13px] leading-relaxed text-ink-500">{detail}</p>}
    </div>
  </li>
);

const Chip = ({ icon: Icon, children, tone = "ink" }) => (
  <span
    className={cx(
      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium ring-1",
      tone === "ink" && "bg-white/90 text-ink-700 ring-ink-200/80",
      tone === "emerald" && "bg-emerald-50 text-emerald-700 ring-emerald-200/80",
    )}
  >
    <Icon size={12} className={tone === "ink" ? "text-ink-400" : undefined} aria-hidden />
    {children}
  </span>
);

const Working = ({ slow }) => (
  <section className="overflow-hidden rounded-3xl border border-ink-200/70 bg-white shadow-card-elevated" aria-live="polite">
    <div className="h-1 w-full bg-brand-gradient" aria-hidden />
    <div className="px-7 py-9 sm:px-10">
      <div className="flex items-center gap-4">
        <span className="relative grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-brand-gradient shadow-brand-glow">
          <span className="absolute inset-0 animate-ping-slow rounded-2xl bg-brand-400/30 motion-reduce:hidden" aria-hidden />
          <Blocks size={22} className="relative text-white" />
        </span>
        <div>
          <h1 className="text-[1.4rem] font-bold leading-tight tracking-tight text-ink-900">Finishing your install</h1>
          <p className="mt-0.5 text-[13.5px] text-ink-500">Keep this tab open — it only takes a moment.</p>
        </div>
      </div>

      <ol className="mt-8 space-y-5">
        <Step state="done" title="Authorization received" detail="You approved the app in the marketplace." />
        <Step state="active" title="Connecting your account" detail="Securely linking the app to your CRM account." />
      </ol>

      {slow && (
        <p className="mt-7 flex items-start gap-2 rounded-xl bg-brand-50/70 px-4 py-3 text-[13px] leading-relaxed text-brand-800 ring-1 ring-brand-100 anim-in">
          <Users size={15} className="mt-0.5 shrink-0" aria-hidden />
          Still working. When the app is installed across an agency, every account is set up one at a time — this can take
          a minute.
        </p>
      )}
    </div>
  </section>
);

const Done = ({ app, appId, safeMessage }) => {
  const name = app?.appName || "Your app";
  const agencyWide = app?.userType === "Company" || Boolean(app?.locationsInstalled);

  return (
    <section className="overflow-hidden rounded-3xl border border-ink-200/70 bg-white shadow-card-elevated">
      {/* Identity band — the app's own tile, so the installer can see at a
          glance which of several installs just completed. */}
      <header className="relative px-7 pb-7 pt-9 text-center sm:px-10">
        <div className="pointer-events-none absolute inset-0 bg-brand-subtle" aria-hidden />
        <div className="pointer-events-none absolute inset-0 bg-mesh-1" aria-hidden />

        <div className="relative">
          <span className="relative mx-auto inline-block rounded-[1.4rem] bg-white p-1.5 shadow-card-elevated ring-1 ring-ink-200/60">
            <AppIcon app={{ appName: app?.appName, appImage: app?.appImage, appId }} size="xl" />
            <span className="absolute -bottom-1.5 -right-1.5 grid h-7 w-7 place-items-center rounded-full bg-emerald-500 text-white ring-4 ring-white">
              <Check size={14} strokeWidth={3} aria-hidden />
            </span>
          </span>

          <h1 className="mx-auto mt-5 max-w-md text-balance text-[1.6rem] font-bold leading-tight tracking-tight text-ink-900">
            {name} is installed
          </h1>
          <p className="mx-auto mt-1.5 max-w-md text-[14px] leading-relaxed text-ink-600">
            Your account is connected. One last step and it's ready to use.
          </p>

          <div className="mt-4 flex flex-wrap justify-center gap-1.5">
            <Chip icon={CheckCircle2} tone="emerald">
              Connected
            </Chip>
            {agencyWide ? (
              <Chip icon={Building2}>Agency-wide install</Chip>
            ) : (
              app?.locationId && <Chip icon={MapPin}>Installed on this account</Chip>
            )}
            {app?.locationsInstalled > 0 && (
              <Chip icon={Users}>
                Added to {app.locationsInstalled} account{app.locationsInstalled === 1 ? "" : "s"}
              </Chip>
            )}
          </div>
        </div>
      </header>

      {app?.locationsSkipped > 0 && (
        <p className="flex items-start gap-2.5 border-y border-amber-200/70 bg-amber-50 px-7 py-3.5 text-[13px] leading-relaxed text-amber-800 sm:px-10">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden />
          {app.locationsSkipped} account{app.locationsSkipped === 1 ? "" : "s"} couldn't be connected yet. Installing the
          app again will retry {app.locationsSkipped === 1 ? "it" : "them"}.
        </p>
      )}

      <div className="border-t border-ink-150 px-7 py-7 sm:px-10">
        <h2 className="text-[11.5px] font-semibold uppercase tracking-[0.08em] text-ink-500">What to do next</h2>

        {safeMessage ? (
          // Sanitized by the caller. prose-app styles headings, lists and
          // links, so an ordered list authored in the admin renders as steps.
          <div className="prose-app mt-3" dangerouslySetInnerHTML={{ __html: safeMessage }} />
        ) : (
          <ol className="mt-4 space-y-4">
            {[
              ["Go back to your CRM", "You can close this tab — nothing else happens here."],
              [`Open ${name} from the left menu`, "It's listed with your other apps."],
              ["Add your credentials", "The setup guide inside the app walks you through each value."],
            ].map(([title, detail], i) => (
              <li key={title} className="flex gap-3.5">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-50 text-[12.5px] font-semibold text-brand-700 ring-1 ring-brand-200/70">
                  {i + 1}
                </span>
                <div className="min-w-0 pt-0.5">
                  <p className="text-[14px] font-semibold text-ink-900">{title}</p>
                  <p className="mt-0.5 text-[13px] leading-relaxed text-ink-500">{detail}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-ink-150 bg-ink-50/60 px-7 py-4 text-[12px] text-ink-500 sm:px-10">
        <span className="inline-flex items-center gap-1.5">
          <Lock size={12} className="text-ink-400" aria-hidden />
          Your access keys are stored securely on our servers.
        </span>
        <span>It's safe to close this tab.</span>
      </footer>
    </section>
  );
};

const Failed = ({ message }) => (
  <section className="overflow-hidden rounded-3xl border border-ink-200/70 bg-white shadow-card-elevated" role="alert">
    <div className="h-1 w-full bg-rose-500" aria-hidden />
    <div className="px-7 py-9 sm:px-10">
      <div className="flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-rose-50 ring-1 ring-rose-200">
          <AlertCircle size={22} className="text-rose-600" aria-hidden />
        </span>
        <div className="min-w-0">
          <h1 className="text-[1.4rem] font-bold leading-tight tracking-tight text-ink-900">The install didn't finish</h1>
          <p className="mt-1 text-[14px] leading-relaxed text-ink-600">
            Your account wasn't connected this time. Starting the install again usually fixes it.
          </p>
        </div>
      </div>

      {message && (
        <div className="mt-6 rounded-xl border border-ink-200/80 bg-ink-50/70 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-400">What went wrong</p>
          <p className="mt-1 break-words text-[13.5px] leading-relaxed text-ink-700">{message}</p>
        </div>
      )}

      <h2 className="mt-7 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-ink-500">How to fix it</h2>
      <ol className="mt-3 space-y-3">
        {[
          "Go back to the marketplace and open the app.",
          "Click Install and approve it again — each install link only works once, so refreshing this page won't help.",
          "Still stuck? Contact us from the app's support page and we'll sort it out.",
        ].map((text, i) => (
          <li key={i} className="flex gap-3 text-[13.5px] leading-relaxed text-ink-700">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ink-100 text-[12px] font-semibold text-ink-600">
              {i + 1}
            </span>
            <span className="pt-0.5">{text}</span>
          </li>
        ))}
      </ol>

    </div>
  </section>
);

const Integration = () => {
  const { appId = "" } = useParams();
  const [params] = useSearchParams();
  const { auth } = useAppServices();

  const code = params.get("code") || "";

  const [status, setStatus] = useState("working"); // working | done | error
  const [message, setMessage] = useState("");
  const [app, setApp] = useState(null);
  const [slow, setSlow] = useState(false);

  // The code is single-use — a second run would burn it and report a spurious
  // failure, so this fires at most once per mount.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!code) {
      setStatus("error");
      setMessage("No authorization code was in the address. Start the install from the marketplace.");
      return;
    }

    if (!appId) {
      setStatus("error");
      setMessage("No app ID in the address. The redirect URI registered for this app must end in /integration/<app id>.");
      return;
    }

    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);

    (async () => {
      const { response, error } = await auth.IntegrateApp({ code, appId });
      clearTimeout(timer);

      if (error) {
        setStatus("error");
        setMessage(error?.message || "The install could not be completed.");
        return;
      }

      setApp(response || null);
      setStatus("done");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Authored in the admin's rich editor, so it arrives as HTML and has to be
  // sanitized before it goes anywhere near dangerouslySetInnerHTML.
  const safeMessage = useMemo(() => {
    const raw = app?.installMessage || "";
    return raw ? DOMPurify.sanitize(raw) : "";
  }, [app]);

  useEffect(() => {
    document.title =
      status === "done" ? `${app?.appName || "App"} installed` : status === "error" ? "Install didn't finish" : "Finishing install…";
  }, [status, app]);

  return (
    <Shell>
      {status === "working" && <Working slow={slow} />}
      {status === "done" && <Done app={app} appId={appId} safeMessage={safeMessage} />}
      {status === "error" && <Failed message={message} />}
    </Shell>
  );
};

export default Integration;
